import { FormikHelpers } from 'formik';

export type FormSubmissionResult<Data> =
  | { type: 'success'; data: Data }
  | { type: 'validation-error'; errors: { [name: string]: string[] } }
  | { type: 'error'; error: string };

/** A **common layer** for submitting forms, handling validation and errors.
 *
 * - This would be a good place for extending or wrapping any cross-cutting
 *   form submission concerns.
 */
export const useFormSubmission = <Values, Data>(
  onSubmit: (values: Values) => Promise<FormSubmissionResult<Data>>,
  onSuccess: (data: Data) => void
) => {
  const inner = async (values: Values, { setSubmitting, setStatus }: FormikHelpers<Values>) => {
    try {
      setSubmitting(true);
      setStatus({});

      const response = await onSubmit(values);
      setSubmitting(false);

      switch (response.type) {
        case 'success':
          onSuccess(response.data);
          break;

        case 'error':
          setStatus({ __server_error: response.error });
          break;

        case 'validation-error':
          setStatus(response.errors);
          break;
      }
    } catch (e: any) {
      setStatus({ __server_error: 'Something went wrong. Please try again in a minute.' });
      console.error('error', e);
      setSubmitting(false);
    }
  };

  return { onSubmit: inner };
};
